/** @file Interleaves positions, UVs and colour words into the vertex stream with change tracking. */
import { RENDER_LAYOUT, type RenderBuffers } from '../renderBuffers';
import type { BuildState } from './buildState';

const VW = RENDER_LAYOUT.VERTEX_WORDS;

/**
 * Writes `count` vertices at `st.v`: x, y from `pos` (2 per vertex), u, v from `uvs`, then the light and dark
 * words; sets `st.changed` when any word differs bitwise, grows the running bounds, advances `st.v`.
 */
export function writeVertices(out: RenderBuffers, st: BuildState, pos: Float32Array, uvs: Float32Array, count: number, light: number, dark: number): void {
  const f32 = out.vertexF32, u32 = out.vertexU32;
  let changed = false;
  for (let k = 0; k < count; k++) {
    const o = (st.v + k) * VW;
    const w0 = u32[o], w1 = u32[o + 1], w2 = u32[o + 2], w3 = u32[o + 3];
    f32[o] = pos[2 * k] ?? 0;
    f32[o + 1] = pos[2 * k + 1] ?? 0;
    f32[o + 2] = uvs[2 * k] ?? 0;
    f32[o + 3] = uvs[2 * k + 1] ?? 0;
    if (u32[o] !== w0 || u32[o + 1] !== w1 || u32[o + 2] !== w2 || u32[o + 3] !== w3 || u32[o + 4] !== light || u32[o + 5] !== dark) changed = true;
    u32[o + 4] = light;
    u32[o + 5] = dark;
    const x = f32[o] ?? 0, y = f32[o + 1] ?? 0;
    if (x < st.minX) st.minX = x;
    if (x > st.maxX) st.maxX = x;
    if (y < st.minY) st.minY = y;
    if (y > st.maxY) st.maxY = y;
  }
  if (changed) st.changed = true;
  st.v += count;
}
